import { useEffect, useState } from 'react';
import { Ship, Users, MapPin, Award, Target, Eye, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

export const About = () => {
  const { t, language } = useLanguage();
  const [counters, setCounters] = useState({ operations: 0, clients: 0, ports: 0 });

  const stats = [
    { key: 'operations', value: 450, suffix: 'K', label_en: 'Completed Operations', label_ru: 'Выполненных операций' },
    { key: 'clients', value: 15, suffix: 'K', label_en: 'Global Clients', label_ru: 'Глобальных клиентов' },
    { key: 'ports', value: 30, suffix: '', label_en: 'Ports Served', label_ru: 'Обслуживаемых портов' },
  ];

  useEffect(() => {
    stats.forEach((stat) => {
      let current = 0;
      const increment = stat.value / 40;
      const timer = setInterval(() => {
        current += increment;
        if (current >= stat.value) {
          current = stat.value;
          clearInterval(timer);
        }
        setCounters(prev => ({ ...prev, [stat.key]: Math.round(current) }));
      }, 50);
    });
  }, []);

  return (
    <main data-testid="about-page" className="min-h-screen pt-24">
      {/* Hero - Asymmetric */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="container-bespoke">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <p className="text-editorial text-slate-500 mb-6">
                {language === 'en' ? 'Our Story' : 'Наша история'}
              </p>
              <h1
                data-testid="about-title"
                className="font-heading text-5xl lg:text-6xl xl:text-7xl font-extrabold text-navy mb-8 leading-tight"
              >
                {language === 'en' 
                  ? 'Three decades of maritime excellence'
                  : 'Три десятилетия морского мастерства'}
              </h1>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-xl text-slate-600 leading-relaxed">
                {language === 'en'
                  ? 'Founded in 1990 by the Levend family, Rota Maritime Agency is a leading maritime agency providing ship agency services across Turkey.'
                  : 'Основанное в 1990 году семьей Левенд, Rota Maritime Agency является ведущим морским агентством.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Full Width Image */}
      <section className="h-[60vh] lg:h-[70vh] relative">
        <img
          src="https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/fmbae7xi_3M6C2379-scaled.jpg"
          alt="Rota Maritime Agency - Cruise Ship Operations"
          className="absolute inset-0 w-full h-full object-cover object-bottom"
        />
        <div className="absolute inset-0 bg-navy/10" />
      </section>

      {/* Story Content */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-bespoke">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            <div>
              <p className="text-editorial text-slate-500 mb-6">
                {language === 'en' ? 'Who We Are' : 'Кто мы'}
              </p>
              <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-navy mb-8">
                {language === 'en' 
                  ? 'A Legacy of Maritime Excellence'
                  : 'Наследие морского мастерства'}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {language === 'en'
                  ? 'Founded in 1990 by the Levend family, Rota Maritime Agency is a leading maritime agency providing ship agency, Cruise ship agency, and yacht agency services across Turkey. With decades of experience in the maritime industry, we are committed to delivering high-quality, reliable, and efficient services to our clients.'
                  : 'Основанное в 1990 году семьей Левенд, Rota Maritime Agency является ведущим морским агентством, предоставляющим услуги судового агентства, круизного агентства и яхтенного агентства по всей Турции.'}
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {language === 'en'
                  ? 'Headquartered in Sinop, Rota Maritime Agency has built a strong reputation based on professionalism, expertise, and customer satisfaction. Our dedicated team ensures seamless operations for vessels, cruise ship, and yacht owners, offering comprehensive agency services at all major ports throughout Turkey.'
                  : 'Штаб-квартира в Синопе, Rota Maritime Agency построило прочную репутацию, основанную на профессионализме, экспертизе и удовлетворенности клиентов. Наша команда обеспечивает бесперебойную работу для судов во всех крупных портах Турции.'}
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                {language === 'en'
                  ? 'At Rota Maritime Agency, our mission is to facilitate smooth and efficient maritime operations, ensuring that every client receives personalized and expert service. Whether it\'s commercial shipping, passenger cruise handling, or luxury yacht support, we are here to provide the best solutions with our deep industry knowledge and extensive network.'
                  : 'В Rota Maritime Agency наша миссия - обеспечивать плавные и эффективные морские операции, гарантируя персонализированное и экспертное обслуживание каждому клиенту.'}
              </p>
            </div>
            <div className="space-y-12">
              {/* Commitment */}
              <div className="bg-slate-50 p-8">
                <p className="text-slate-600 text-lg leading-relaxed italic">
                  {language === 'en'
                    ? '"With a passion for the sea and a commitment to excellence, Rota Maritime Agency continues to be a trusted partner in the maritime industry. Join us as we navigate towards a future of innovation and seamless maritime services."'
                    : '"Со страстью к морю и стремлением к совершенству, Rota Maritime Agency продолжает оставаться надежным партнером в морской отрасли."'}
                </p>
              </div>
              
              {/* Mission */}
              <div data-testid="mission-card">
                <div className="flex items-center gap-4 mb-4">
                  <Target className="w-6 h-6 text-signal" />
                  <h3 className="font-heading text-xl font-bold text-navy">
                    {t('about.mission.title')}
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed pl-10">
                  {t('about.mission.text')}
                </p>
              </div>
              
              {/* Vision */}
              <div data-testid="vision-card">
                <div className="flex items-center gap-4 mb-4">
                  <Eye className="w-6 h-6 text-ocean" />
                  <h3 className="font-heading text-xl font-bold text-navy">
                    {t('about.vision.title')}
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed pl-10">
                  {t('about.vision.text')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Membership Logos */}
          <div className="flex flex-wrap justify-center items-center gap-12 lg:gap-20 mt-16 pt-12 border-t border-slate-200">
            {/* Deniz Ticaret Odası */}
            <div className="flex items-center justify-center overflow-hidden rounded-full" style={{ width: '100px', height: '100px' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_4ee41947-445a-46ec-9561-bbd2b28a30e8/artifacts/bx0ep7um_DTO_Logo_Yuvarlak_tr-eng-_2_.jpg" 
                alt="Deniz Ticaret Odası" 
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Ulaştırma ve Altyapı Bakanlığı */}
            <div className="flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_4ee41947-445a-46ec-9561-bbd2b28a30e8/artifacts/hucg49s8_Tu%CC%88rkiye_Cumhuriyeti_Ulas%CC%A7t%C4%B1rma%2C_Denizcilik_ve_Haberles%CC%A7me_Bakanl%C4%B1g%CC%86%C4%B1_logo.png" 
                alt="T.C. Ulaştırma Bakanlığı" 
                className="h-24 w-auto object-contain"
              />
            </div>
            
            {/* Türkiye İhracatçılar Meclisi */}
            <div className="flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_4ee41947-445a-46ec-9561-bbd2b28a30e8/artifacts/iac46sls_default-tr.png" 
                alt="Türkiye İhracatçılar Meclisi" 
                className="h-24 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section data-testid="stats-section" className="py-24 lg:py-32 bg-navy">
        <div className="container-bespoke">
          <div className="grid grid-cols-3 gap-8 lg:gap-16">
            {stats.map((stat) => (
              <div key={stat.key} data-testid={`stat-${stat.key}`} className="text-center">
                <p className="font-mono text-4xl lg:text-6xl font-bold text-white mb-3">
                  +{counters[stat.key]}{stat.suffix}
                </p>
                <p className="text-editorial text-slate-400">
                  {language === 'en' ? stat.label_en : stat.label_ru}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-white border-t border-slate-200">
        <div className="container-bespoke text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-navy mb-8">
            {language === 'en' ? 'Ready to work together?' : 'Готовы к сотрудничеству?'}
          </h2>
          <Link to="/contact">
            <button className="btn-signal">
              {language === 'en' ? 'Contact Us' : 'Связаться'}
              <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </main>
  );
};
